import {
  gaqlchainIs as chainIs
} from '../gaqlchain.js'

import {
  gaqlasyncarrSome as asyncarrSome,
  gaqlasyncarrEvery as asyncarrEvery,
  gaqlasyncarrReduce as asyncarrReduce
} from '../gaqlasyncarr.js'

import {
  gaqlutilQueryArgsOptGet as utilQueryArgsOptGet
} from '../gaqlutil.js'

import {
  gaqlerrArgsNumber as errArgsNumber,
  gaqlerrCannotReduceOverEmptyStream as errCannotReduceOverEmptyStream,
  gaqlerrExpectedTypeFOOFound as errExpectedTypeFOOFound
} from '../gaqlerr.js'


// Produce a single value from a sequence through repeated application of a
// reduction function.
//
// The reduction function can be called on:
//
//  * two elements of the sequence
//  * one element of the sequence and one result of a previous reduction
//  * two results of previous reductions
//
// The reduction function can be called on the results of two previous
// reductions because the reduce command is distributed and parallelized
// across shards and CPU cores. A common mistaken when using the reduce
// command is to suppose that the reduction is executed from left to right.
// Read the map-reduce in RethinkDB article to see an example.
//
// If the sequence is empty, the server will produce a ReqlRuntimeError
// that can be caught with default.
//
// TAKECARE: when shape of reduced value differs from shape of sequence values
//
// await q.expr([
//   { count: 3 }, { count: 0 },
//   { count: 6 }, { count: 7 }
// ]).reduce((left, right) => (
//   left('count').add(right('count').add(5))
// )).run()
//
// > 'Cannot perform bracket on a non-object non-sequence `8`.'
//
// ```
// sequence.reduce(function) → value
// q.reduce(sequence, function) → value
// ```
const $reduce = async (gst, qst, args, run) => {
  if (args.length === 0) {
    throw errArgsNumber('reduce', 1, args.length)
  }

  // live rethinkdb inst returns sequence of 0 as error
  if (qst.target.length === 0) {
    throw errCannotReduceOverEmptyStream()
  }

  // live rethinkdb inst returns sequence of 1 atom
  if (qst.target.length === 1) {
    qst.target = qst.target[0]

    return qst
  }

  const seq = qst.target.sort(() => 0.5 - Math.random())

  qst.target = await asyncarrReduce(seq.slice(1), async (ac, arg) => (
    run(gst, qst, args[0], [ac, arg])), seq[0])

  return qst
}

// Apply a function to a sequence in order, maintaining state via an
// accumulator. The fold command returns either a single value or a new
// sequence.
//
// In its first form, fold operates like reduce, returning a value by applying
// a combining function to each element in a sequence. The combining function
// takes two parameters: the previous reduction result (the accumulator) and the
// current element. However, fold has the following differences from reduce:
//  * it is guaranteed to proceed through the sequence from first element to
//    last.
//  * it passes an initial base value to the function with the first element in
//    place of the previous reduction result.
//
// ```
// combiningFunction(accumulator | base, element) → newAccumulator
// ```
//
// In its second form, fold operates like concatMap, returning a new sequence
// rather than a single value. When an emit function is provided, fold will:
//  * proceed through the sequence in order and take an initial base value,
//    as above.
//  * for each element in the sequence, call both the combining function and
//    a separate emitting function. The emitting function takes three
//    parameters: the previous reduction result (the accumulator), the current
//    element, and the output from the combining function (the new value of the
//    accumulator).
//
// If provided, the emitting function must return a list.
// ```
// emit(previousAccumulator, element, accumulator) → array
// ```
// A finalEmit function may also be provided, which will be called at the end
// of the sequence. It takes a single parameter: the result of the last
// reduction through the iteration (the accumulator), or the original base value
// if the input sequence was empty. This function must return a list, which will
// be appended to fold’s output stream.
// ```
// finalEmit(accumulator | base) → array
// ```
//
// ```
// sequence.fold(base, function) → value
// sequence.fold(base, function, {
//   emit: function[, finalEmit: function]}) → sequence
// ```
const $fold = async (gst, qst, args, run) => {
  const startVal = await run(gst, qst, args[0])
  const reduceFn = args[1]

  if (args.length < 2) {
    throw errArgsNumber('fold', 2, args.length)
  }

  qst.target = await asyncarrReduce(qst.target, async (ac, arg) => (
    run(gst, qst, reduceFn, [ac, arg])
  ), startVal)

  return qst
}

// Takes a stream and partitions it into multiple groups based on the fields
// or functions provided.
//
// With the multi flag single documents can be assigned to multiple groups,
// similar to the behavior of multi-indexes. When multi is true and the grouping
// value is an array, documents will be placed in each group that corresponds to
// the elements of the array. If the array is empty the row will be ignored.
// ```
// sequence.group(field | function..., [{index: <indexname>, multi: false}])
//   → grouped_stream
// q.group(sequence, field | function..., [{index: <indexname>, multi: false}])
//   → grouped_stream
// ```
const $group = async (gst, qst, args, run) => {
  const queryTarget = qst.target
  const multi = Boolean(utilQueryArgsOptGet(args, {}).multi)
  const arg = args[0]

  const groupAddItem = (groups, groupname, item) => (
    groups[groupname] = groups[groupname] || [],
    groups[groupname].push(item),
    groups)

  const groupedData = await asyncarrReduce(queryTarget, async (ac, item) => {
    const key = (typeof arg === 'object' && arg && 'index' in arg)
      ? arg.index
      : await run(gst, qst, arg, [item])

    ac = multi
      ? key.reduce((acc, k) => groupAddItem(acc, k, item), ac)
      : groupAddItem(ac, key, item)

    return ac
  }, {})

  const rethinkFormat = Object.entries(groupedData)
    .map(([group, reduction]) => ({ group, reduction }))

  qst.isGrouped = true
  qst.target = rethinkFormat

  return qst
}

// Takes a grouped stream or grouped data and turns it into an array of objects
// representing the groups. Any commands chained after ungroup will operate on
// this array, rather than operating on each group individually. This is useful
// if you want to e.g. order the groups by the value of their reduction.
//
// The format of the array returned by ungroup is the same as the default native
// format of grouped data in the javascript driver and data explorer.
// ```
// grouped_stream.ungroup() → array
// grouped_data.ungroup() → array
// ```
const $ungroup = (gst, qst) => {
  qst.isGrouped = false

  return qst
}

// Counts the number of elements in a sequence or key/value pairs in an object,
// or returns the size of a string or binary object.
//
// When count is called on a sequence with a predicate value or function, it
// returns the number of elements in the sequence equal to that value or where
// the function returns true. On a binary object, count returns the size of the
// object in bytes; on strings, count returns the string’s length. This is
// determined by counting the number of Unicode codepoints in the string,
// counting combining codepoints separately.
// ```
// sequence.count([value | predicate_function]) → number
// binary.count() → number
// string.count() → number
// object.count() → number
// array.count() → number
// q.count(sequence | binary | string | object[, predicate_function]) → number
// ```
const $count = (gst, qst) => {
  qst.target = qst.target.length

  return qst
}

// Finds the minimum element of a sequence.
//
// The min command can be called with:
//  * a field name, to return the element of the sequence with the smallest
//    value in that field;
//  * an index (the primary key or a secondary index), to return the element of
//    the sequence with the smallest value in that index;
//  * a function, to apply the function to every element within the sequence and
//    return the element which returns the smallest value from the function,
//    ignoring any elements where the function produces a non-existence error.
//
// For more information on RethinkDB’s sorting order, read the section in ReQL
// data types.
//
// Calling min on an empty sequence will throw a non-existence error; this can
// be handled using the default command.
// ```
// sequence.min(field | function) → element
// sequence.min({index: <indexname>}) → element
// q.min(sequence, field | function) → element
// q.min(sequence, {index: <indexname>}) → element
// ```
const $min = async (gst, qst, args) => {
  const targetList = qst.target
  const getListMin = (list, prop) => list.reduce((maxDoc, doc) => (
    maxDoc[prop] < doc[prop] ? maxDoc : doc
  ), targetList)

  const getListMinGroups = (groups, prop) => (
    groups.reduce((prev, target) => {
      prev.push({
        ...target,
        reduction: getListMin(target.reduction, prop)
      })

      return prev
    }, [])
  )

  qst.target = qst.isGrouped
    ? getListMinGroups(targetList, args[0])
    : getListMin(targetList, args[0])

  return qst
}

// Finds the maximum element of a sequence.
//
// The max command can be called with:
//
//  * a field name, to return the element of the sequence with the largest value
//    in that field;
//  * an index (the primary key or a secondary index), to return the element of
//    the sequence with the largest value in that index;
//  * a function, to apply the function to every element within the sequence and
//    return the element which returns the largest value from the function,
//    ignoring any elements where the function produces a non-existence error.
//
// For more information on RethinkDB’s sorting order, read the section in ReQL
// data types.
//
// Calling max on an empty sequence will throw a non-existence error; this can
// be handled using the default command.
// ```
// sequence.max(field | function) → element
// sequence.max({index: <indexname>}) → element
// q.max(sequence, field | function) → element
// q.max(sequence, {index: <indexname>}) → element
// ```
const $max = async (gst, qst, args) => {
  const targetList = qst.target
  const getListMax = (list, prop) => list.reduce((maxDoc, doc) => (
    maxDoc[prop] > doc[prop] ? maxDoc : doc
  ), targetList)

  const getListMaxGroups = (groups, prop) => (
    groups.reduce((prev, target) => {
      prev.push({
        ...target,
        reduction: getListMax(target.reduction, prop)
      })

      return prev
    }, [])
  )

  qst.target = qst.isGrouped
    ? getListMaxGroups(targetList, args[0])
    : getListMax(targetList, args[0])

  return qst
}

// Removes duplicates from elements in a sequence.
//
// The distinct command can be called on any sequence or table with an index.
// ```
// sequence.distinct() → array
// table.distinct([{index: <indexname>}]) → stream
// q.distinct(sequence) → array
// q.distinct(table, [{index: <indexname>}]) → stream
// ```
const $distinct = async (gst, qst, args) => {
  const index = utilQueryArgsOptGet(args, {}).index

  if (Array.isArray(qst.target) && index) {
    const keys = {}
    qst.target = qst.target.reduce((disti, row) => {
      const value = row[index]

      if (!keys[value]) {
        keys[value] = true
        disti.push(row)
      }

      return disti
    }, [])
  } else if (Array.isArray(qst.target)) {
    qst.target = qst.target.filter(
      (item, pos, self) => self.indexOf(item) === pos)
  } else if (Array.isArray(args[0])) {
    qst.target = args[0].filter(
      (item, pos, self) => self.indexOf(item) === pos)
  }

  return qst
}

// When called with values, returns true if a sequence contains all the
// specified values. When called with predicate functions, returns true if for
// each predicate there exists at least one element of the stream where that
// predicate returns true.
//
// Values and predicates may be mixed freely in the argument list.
// ```
// sequence.contains([value | predicate_function, ...]) → bool
// q.contains(sequence, [value | predicate_function, ...]) → bool
// ```
const $contains = async (gst, qst, args, run) => {
  const queryTarget = qst.target

  if (!Array.isArray(qst.target))
    throw errExpectedTypeFOOFound('SEQUENCE', 'SINGLE_SELECTION')

  if (chainIs(args[0])) {
    qst.target = await asyncarrSome(queryTarget, async target => {
      const res = await run(gst, qst, args[0], [target])

      return typeof res === 'boolean'
        ? res
        : queryTarget.includes(res)
    })
  } else {
    qst.target = await asyncarrEvery(args, async predicate => (
      queryTarget.includes(await run(gst, qst, predicate))))
  }

  return qst
}

export {
  $reduce,
  $fold,
  $group,
  $ungroup,
  $count,
  $min,
  $max,
  $distinct,
  $contains
}
